/**
 * Built-in validation rules + the rule registry.
 *
 * A rule definition is:
 *   {
 *     message: 'Default message with {param} / {value} / {label} placeholders',
 *     validate(value, ctx) -> true | false | string | Promise of those
 *   }
 * Returning a string means "invalid, and here is the specific message" (shown
 * as-is - rule authors translate their own strings). `false` uses the default
 * message (translated through g.translate, then the placeholders are filled).
 *
 * ctx: { el, form, rule, param, attach, valueOf(name) }. Rules other than
 * `required` are never called on an empty value (optional field = valid when
 * empty) - the runner in validator.js guarantees that.
 *
 * `server` is NOT here: it is handled by the runner itself (g.fetch + result
 * cache). Its name plus the `-message` / `-attach` suffixes are reserved.
 */

// Empty = missing for `required` and for the "skip rules when optional and
// empty" behavior. A file jsonb object counts as a value; whitespace does not.
export const isEmpty = (value) => {
	if (value == null) return true;
	if (typeof value === 'string') return value.trim() === '';
	if (Array.isArray(value)) return value.length === 0;
	return false;
};

// How many "items" a value holds (multi-select, checkbox group, file list).
export const itemCount = (value) => {
	if (Array.isArray(value)) return value.length;
	return isEmpty(value) ? 0 : 1;
};

const isNumeric = (value) => typeof value !== 'object' && value !== '' && value != null && !isNaN(Number(value));

export const registry = {
	required: {
		message: 'This field is required.',
		validate: (value) => !isEmpty(value),
	},
	min: {
		message: 'The value must be at least {param}.',
		validate: (value, ctx) => isNumeric(value) && Number(value) >= Number(ctx.param),
	},
	max: {
		message: 'The value must be at most {param}.',
		validate: (value, ctx) => isNumeric(value) && Number(value) <= Number(ctx.param),
	},
	'min-length': {
		message: 'Please enter at least {param} characters.',
		validate: (value, ctx) => String(value).length >= Number(ctx.param),
	},
	'max-length': {
		message: 'Please enter at most {param} characters.',
		validate: (value, ctx) => String(value).length <= Number(ctx.param),
	},
	'min-items': {
		message: 'Please select at least {param} items.',
		validate: (value, ctx) => itemCount(value) >= Number(ctx.param),
	},
	'max-items': {
		message: 'Please select at most {param} items.',
		validate: (value, ctx) => itemCount(value) <= Number(ctx.param),
	},
	email: {
		message: 'Please enter a valid email address.',
		validate: (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value)),
	},
	url: {
		message: 'Please enter a valid URL.',
		validate: (value) => {
			try {
				const u = new URL(String(value));
				return u.protocol === 'http:' || u.protocol === 'https:';
			} catch {
				return false;
			}
		},
	},
	number: {
		message: 'Please enter a valid number.',
		validate: (value) => isNumeric(value),
	},
	integer: {
		message: 'Please enter a whole number.',
		validate: (value) => /^-?\d+$/.test(String(value).trim()),
	},
	pattern: {
		message: 'The value has an invalid format.',
		validate: (value, ctx) => {
			try {
				return new RegExp('^(?:' + ctx.param + ')$').test(String(value));
			} catch {
				return false;
			}
		},
	},
	'same-as': {
		message: 'The values do not match.',
		validate: (value, ctx) => String(value) === String(ctx.valueOf(ctx.param) ?? ''),
	},
	// The house password policy: at least 10 characters; up to 20 characters it
	// must also mix an uppercase letter, a lowercase letter and a special
	// character (anything that is not a letter or a digit, Unicode-wide - so
	// spaces, diacritics, CJK all count as characters, and any non-letter/digit
	// satisfies "special"). Over 20 characters, length alone is enough. The
	// error lists exactly what is missing.
	password: {
		message: 'The password does not meet the requirements.',
		validate: (value) => {
			const v = String(value);
			const missing = [];
			if (v.length < 10) missing.push(window.g.translate('at least 10 characters'));
			if (v.length <= 20) {
				if (!/\p{Lu}/u.test(v)) missing.push(window.g.translate('an uppercase letter'));
				if (!/\p{Ll}/u.test(v)) missing.push(window.g.translate('a lowercase letter'));
				if (!/[^\p{L}\p{N}]/u.test(v)) missing.push(window.g.translate('a special character'));
			}
			if (!missing.length) return true;
			return window.g.translate('The password must contain {list}.', { list: missing.join(', ') });
		},
	},
};

// g.validator.register(name, def) - also fed from g.init({ validator: { rules } }).
export function register(name, def) {
	if (!name || !def || typeof def.validate !== 'function') return;
	registry[name] = def;
}
